import { QueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { Logger } from 'src/utils/Logger';
import { GenerateSuggestionsResponse } from './useSuggestedActions';

type DocumentVersion = {
  id: string;
  markdownContent?: string;
  isPrimary: boolean;
};

/**
 * Prefetch suggested actions for a document into the React Query cache.
 * Uses the same cache key ['suggested-actions', documentId] and staleTime as useSuggestedActions,
 * so prefetched data is returned instantly when the edit page loads.
 *
 * Can be called from list pages (on generation completion) or edit pages (after finalise).
 * If documentContent is not provided, fetches document versions to get it.
 */
export async function prefetchSuggestedActions(
  queryClient: QueryClient,
  documentId: string,
  clientId: string,
  documentContent?: string,
): Promise<void> {
  try {
    let content = documentContent;

    if (!content) {
      const { data: versions } = await api.get<DocumentVersion[]>(
        `documents/${documentId}/versions`,
      );
      const primary = versions.find((v: DocumentVersion) => v.isPrimary) ?? versions[0];
      content = primary?.markdownContent;
    }

    if (!content) return;

    // Remove any existing cached data so prefetchQuery always fetches fresh results.
    // Without this, staleTime: Infinity would cause prefetchQuery to skip the fetch
    // when data already exists (e.g., after finalise with updated content).
    queryClient.removeQueries({ queryKey: ['suggested-actions', documentId] });

    await queryClient.prefetchQuery({
      queryKey: ['suggested-actions', documentId],
      queryFn: async () => {
        const { data } = await api.post<GenerateSuggestionsResponse>(
          `documents/${documentId}/suggested-actions/generate`,
          { documentContent: content, clientId },
        );
        return data;
      },
      staleTime: Infinity,
    });
  } catch (error) {
    Logger.error(
      'Error during suggestions prefetch',
      error instanceof Error ? error : new Error(String(error)),
      { documentId },
    );
  }
}
